import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigationType } from 'react-router-dom';
import { DocumentsAPI, CategoriesAPI } from '../services/api';

/* ── helpers ─────────────────────────────────────────────────────────── */

const fmtDate = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const months = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  return m ? `${months[+m - 1]} ${y}` : y;
};

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const fileExt = (f) => (f || '').split('.').pop().toLowerCase();

/* ── file type badge ─────────────────────────────────────────────────── */

function FileTypeBadge({ filename }) {
  const ext = fileExt(filename);
  const cfg = ext === 'pdf'
    ? { label: 'PDF', bg: 'rgba(176,48,48,.1)', color: 'var(--danger)' }
    : ext === 'doc' || ext === 'docx'
    ? { label: 'DOC', bg: 'rgba(61,107,122,.1)', color: 'var(--lake)' }
    : ext === 'ppt' || ext === 'pptx'
    ? { label: 'PPT', bg: 'rgba(214,126,44,.1)', color: '#d67e2c' }
    : ext === 'xls' || ext === 'xlsx'
    ? { label: 'XLS', bg: 'rgba(34,124,50,.1)', color: '#227c32' }
    : ext === 'jpg' || ext === 'jpeg'
    ? { label: 'JPG', bg: 'rgba(76,140,106,.1)', color: '#4c8c6a' }
    : ext === 'png'
    ? { label: 'PNG', bg: 'rgba(108,90,160,.1)', color: '#6c5aa0' }
    : { label: 'FIL', bg: 'rgba(44,74,46,.1)', color: 'var(--forest)' };
  return (
    <span style={{
      flexShrink: 0,
      fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
      padding: '0.22rem 0.55rem', borderRadius: 3,
      background: cfg.bg, color: cfg.color
    }}>
      {cfg.label}
    </span>
  );
}

/* ── document row ────────────────────────────────────────────────────── */

function DocRow({ doc, isMobile }) {
  const [downloading, setDownloading] = useState(false);
  const hasFile = doc.fil_url || doc.fil;
  const isLink = hasFile && hasFile !== '#';

  const handleDownload = async () => {
    if (!isLink || downloading) return;
    setDownloading(true);
    try {
      const response = await DocumentsAPI.download(doc.id);

      let blobUrl, filename;
      if (response.url) {
        const fileRes = await fetch(response.url);
        if (!fileRes.ok) throw new Error(`Storage error: ${fileRes.status}`);
        const blob = await fileRes.blob();
        blobUrl = window.URL.createObjectURL(blob);
        filename = response.filename || doc.namn;
      } else if (response.file) {
        const byteArray = Uint8Array.from(atob(response.file), c => c.charCodeAt(0));
        const blob = new Blob([byteArray], { type: response.contentType });
        blobUrl = window.URL.createObjectURL(blob);
        filename = response.filename || doc.namn;
      }

      if (blobUrl) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Kunde inte ladda ner filen. ' + (err.message || ''));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center', gap: '0.9rem',
        padding: '0.85rem 1.1rem', borderRadius: 5,
        border: '1px solid var(--border)',
        background: 'var(--white)',
        opacity: downloading ? 0.7 : 1
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9rem', flex: 1, minWidth: 0 }}>
        <FileTypeBadge filename={doc.fil_namn || doc.fil} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '0.92rem', fontWeight: 500,
            color: 'var(--text)',
            overflow: isMobile ? 'visible' : 'hidden',
            textOverflow: isMobile ? 'clip' : 'ellipsis',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            wordBreak: isMobile ? 'break-word' : 'normal'
          }}>
            {doc.namn}
          </div>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-soft)', marginTop: '0.15rem' }}>
            {fmtDate(doc.datum)}{doc.fil_storlek ? ` · ${fmtSize(doc.fil_storlek)}` : ''}
          </div>
        </div>
      </div>

      {isLink && (
        <button
          onClick={handleDownload}
          disabled={downloading}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            gap: '0.3rem',
            padding: '0.4rem 0.75rem',
            background: 'var(--forest)',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: downloading ? 'wait' : 'pointer',
            fontSize: '0.78rem',
            fontWeight: 600,
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto',
            transition: 'background 0.15s',
            opacity: downloading ? 0.7 : 1
          }}
          onMouseEnter={e => !downloading && (e.target.style.background = 'rgba(44,74,46,0.85)')}
          onMouseLeave={e => e.target.style.background = 'var(--forest)'}
        >
          {downloading ? '⏳' : '⬇'} {downloading ? 'Laddar...' : 'Ladda ner'}
        </button>
      )}
    </div>
  );
}

/* ── main page ───────────────────────────────────────────────────────── */

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigationType = useNavigationType();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    Promise.all([DocumentsAPI.getAll(), CategoriesAPI.getAll()])
      .then(([d, c]) => {
        setDocuments(d.documents || []);
        setCategories(c.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Scroll to top only on new navigation (not back/forward)
  useEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [navigationType]);

  // Get 3 latest public documents for front page display
  const latestDocs = useMemo(() => {
    return documents
      .filter(d => !(d.is_protected || d.isProtected))
      .sort((a, b) => (b.datum || '').localeCompare(a.datum || ''))
      .slice(0, 3);
  }, [documents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents
      .filter(d => !(d.is_protected || d.isProtected))
      .filter(d => !activeCategory || d.kategori === activeCategory)
      .filter(d => !q || d.namn.toLowerCase().includes(q))
      .sort((a, b) => (b.datum || '').localeCompare(a.datum || ''));
  }, [documents, search, activeCategory]);

  const grouped = useMemo(() => {
    if (activeCategory) return { [activeCategory]: filtered };
    return filtered.reduce((acc, d) => {
      const k = d.kategori || 'Övrigt';
      (acc[k] = acc[k] || []).push(d);
      return acc;
    }, {});
  }, [filtered, activeCategory]);

  // Get categories that have public documents
  const categoriesWithPublicDocs = useMemo(() => {
    const publicDocs = documents.filter(d => !(d.is_protected || d.isProtected));
    const categoryNamesWithDocs = new Set(publicDocs.map(d => d.kategori).filter(Boolean));
    return categories.filter(c => categoryNamesWithDocs.has(c.namn));
  }, [documents, categories]);

  const catOrder = categories.map(c => c.namn);
  const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
    const ai = catOrder.indexOf(a), bi = catOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white, #f7f6f2)' }}>

      {/* ── hero header ── */}
      <div style={{ background: 'var(--forest)', color: '#fff', padding: '3rem 1.5rem 2.5rem' }}>
        <div className="container" style={{ maxWidth: 820 }}>
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
            fontSize: '0.78rem', color: 'rgba(255,255,255,.65)', textDecoration: 'none',
            letterSpacing: '0.04em', marginBottom: '1.25rem'
          }}>
            ← Startsidan
          </Link>
          <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: '2.2rem', margin: '0 0 0.5rem', fontWeight: 700 }}>
            Dokument
          </h1>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,.75)', margin: 0, maxWidth: 520 }}>
            Stadgar, protokoll, Fornbobladet och övriga dokument för Fornbo samfällighetsförening.
          </p>
        </div>
      </div>

      {/* ── filter bar ── */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', position: 'sticky', top: 0, zIndex: 10 }}>
        <div className="container" style={{ maxWidth: 820, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem' }}>

          {/* search */}
          <div style={{ position: 'relative', flexShrink: 0, flexGrow: 1, minWidth: 160, maxWidth: 280 }}>
            <span style={{
              position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text-soft)', fontSize: '0.85rem', pointerEvents: 'none'
            }}>⌕</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Sök dokument..."
              style={{
                width: '100%', padding: '0.5rem 0.75rem 0.5rem 2rem',
                border: '1px solid var(--border)', borderRadius: 4,
                fontSize: '0.88rem', background: 'var(--cream)', color: 'var(--text)',
                outline: 'none'
              }}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{
                position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-soft)', fontSize: '1rem', lineHeight: 1, padding: '0.1rem'
              }}>×</button>
            )}
          </div>

          {/* category pills - only show categories with public documents */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {[{ id: '', namn: 'Alla' }, ...categoriesWithPublicDocs].map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id === '' ? '' : cat.namn)}
                style={{
                  padding: '0.35rem 0.85rem', borderRadius: 20, border: 'none',
                  fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                  letterSpacing: '0.03em',
                  background: activeCategory === (cat.id === '' ? '' : cat.namn)
                    ? 'var(--forest)' : 'var(--cream)',
                  color: activeCategory === (cat.id === '' ? '' : cat.namn)
                    ? '#fff' : 'var(--text-soft)',
                  transition: 'background .15s, color .15s'
                }}
              >
                {cat.namn}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── content ── */}
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>

        {/* result summary */}
        {!loading && (
          <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginBottom: '1.25rem', letterSpacing: '0.02em' }}>
            {filtered.length === 0
              ? 'Inga dokument'
              : `${filtered.length} dokument${activeCategory ? ` i ${activeCategory}` : ''}${search ? ` · "${search}"` : ''}`}
          </div>
        )}

        {/* Latest documents section */}
        {!loading && latestDocs.length > 0 && !search && !activeCategory && (
          <section style={{ marginBottom: '2.5rem' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              marginBottom: '1rem', paddingBottom: '0.5rem',
              borderBottom: '2px solid var(--forest)'
            }}>
              <h2 style={{
                fontFamily: '"Playfair Display", serif',
                fontSize: '1.1rem', color: 'var(--forest)', margin: 0
              }}>
                Senaste dokument
              </h2>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-soft)', fontWeight: 400 }}>
                Senast uppdaterat
              </span>
            </div>
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.6rem',
              background: 'var(--white)',
              borderRadius: 6,
              border: '1px solid var(--border)',
              padding: '1rem',
            }}>
              {latestDocs.map(doc => (
                <div key={doc.id} style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: '0.75rem',
                  padding: '0.6rem 0.75rem',
                  borderRadius: 4,
                  borderBottom: latestDocs.indexOf(doc) < latestDocs.length - 1 ? '1px solid var(--border)' : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                    <FileTypeBadge filename={doc.fil_namn || doc.fil} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '0.88rem', fontWeight: 500,
                        color: 'var(--text)',
                        overflow: isMobile ? 'visible' : 'hidden',
                        textOverflow: isMobile ? 'clip' : 'ellipsis',
                        whiteSpace: isMobile ? 'normal' : 'nowrap',
                        wordBreak: isMobile ? 'break-word' : 'normal'
                      }}>
                        {doc.namn}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-soft)' }}>
                        {fmtDate(doc.datum)}{doc.fil_storlek ? ` · ${fmtSize(doc.fil_storlek)}` : ''}
                      </div>
                    </div>
                  </div>
                  <a
                    href="#"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const response = await DocumentsAPI.download(doc.id);
                        let blobUrl, filename;
                        if (response.url) {
                          const fileRes = await fetch(response.url);
                          if (!fileRes.ok) throw new Error(`Storage error: ${fileRes.status}`);
                          const blob = await fileRes.blob();
                          blobUrl = window.URL.createObjectURL(blob);
                          filename = response.filename || doc.namn;
                        } else if (response.file) {
                          const byteArray = Uint8Array.from(atob(response.file), c => c.charCodeAt(0));
                          const blob = new Blob([byteArray], { type: response.contentType });
                          blobUrl = window.URL.createObjectURL(blob);
                          filename = response.filename || doc.namn;
                        }
                        if (blobUrl) {
                          const link = document.createElement('a');
                          link.href = blobUrl;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
                        }
                      } catch (err) {
                        console.error('Download failed:', err);
                        alert('Kunde inte ladda ner filen. ' + (err.message || ''));
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      gap: '0.3rem',
                      padding: '0.4rem 0.75rem',
                      background: 'var(--forest)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    ⬇ Ladda ner
                  </a>
                </div>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <div className="loading">Laddar dokument...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '5rem 2rem', color: 'var(--text-soft)' }}>
            <div style={{ fontSize: '3rem', opacity: 0.3, marginBottom: '0.75rem' }}>📁</div>
            <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)', marginBottom: '0.4rem' }}>
              Inga dokument hittades
            </div>
            {(search || activeCategory) && (
              <button
                onClick={() => { setSearch(''); setActiveCategory(''); }}
                className="btn btn-ghost"
                style={{ marginTop: '0.75rem', fontSize: '0.78rem' }}
              >
                Rensa filter
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
            {sortedGroups.map(([cat, docs]) => (
              <section key={cat}>
                {/* category header */}
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '0.6rem',
                  marginBottom: '0.75rem', paddingBottom: '0.5rem',
                  borderBottom: '2px solid var(--forest)'
                }}>
                  <h2 style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '1.1rem', color: 'var(--forest)', margin: 0
                  }}>
                    {cat}
                  </h2>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-soft)', fontWeight: 400 }}>
                    {docs.length} {docs.length === 1 ? 'dokument' : 'dokument'}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                  {docs.map(doc => <DocRow key={doc.id} doc={doc} isMobile={isMobile} />)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
